import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { getDefaultProjectModelPrefix } from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type LocalModelContextValue = {
  isNameEdited: boolean;
  isNamespaceEdited: boolean;
  name: string;
  namespace: string;
  setIsNameEdited: (value: boolean) => void;
  setIsNamespaceEdited: (value: boolean) => void;
  setName: (modelName: string) => void;
  setNamespace: (namespace: string) => void;
};

export const LocalModelContext = React.createContext<LocalModelContextValue>({
  isNameEdited: false,
  isNamespaceEdited: false,
  name: '',
  namespace: '',
  setIsNameEdited: noop,
  setIsNamespaceEdited: noop,
  setName: noop,
  setNamespace: noop,
});

export const useLocalModel = () => {
  return React.useContext(LocalModelContext);
};

export const useLocalModelValue = (): LocalModelContextValue => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const [name, setNameFn] = React.useState(
    copy.components.projectModelDialog.localModelPanel.nameExampleText
  );
  const [namespace, setNamespaceFn] = React.useState(() =>
    getDefaultProjectModelPrefix({ name: project?.name || '' })
  );
  const [isNameEdited, setIsNameEdited] = React.useState(false);
  const [isNamespaceEdited, setIsNamespaceEdited] = React.useState(false);

  const setName = React.useCallback(
    (modelName: string) => {
      if (modelName === name) {
        return;
      }

      setNameFn(modelName);
      setIsNameEdited(true);
      if (!isNamespaceEdited && modelName) {
        setNamespaceFn(
          getDefaultProjectModelPrefix({ name: project?.name || '' })
        );
      } else if (!modelName) {
        setNameFn(
          copy.components.projectModelDialog.localModelPanel.nameExampleText
        );
        setNamespaceFn(
          copy.components.projectModelDialog.localModelPanel
            .namespaceExampleText
        );
      }
    },
    [isNamespaceEdited, name, project]
  );

  const setNamespace = React.useCallback(
    (value: string) => {
      if (value === namespace) {
        return;
      }

      if (value) {
        setIsNamespaceEdited(true);
      }
      setNamespaceFn(value);
    },
    [namespace]
  );

  return {
    isNameEdited,
    isNamespaceEdited,
    name,
    namespace,
    setIsNameEdited,
    setIsNamespaceEdited,
    setName,
    setNamespace,
  };
};
